// The admin usage section, browser half.
//
// WHAT IT IS FOR. Not vanity counts. Two questions a bespoke product lives or
// dies on: are they turning up, and have they made it theirs? The second is the
// one with teeth — a client who signs in daily and still runs on our seeded
// limits is reading a weather site, and every workability verdict they have
// acted on was graded on our numbers rather than their own.
//
// SO THE LIMITS LINE IS THE HEADLINE and is stated as a fraction of what could
// be tuned, never as a bare count. "0 of 29" is an answer; "0 tuned" is a shrug.
//
// THE CAVEATS ARE ON THE PAGE, not in a doc nobody opens. Every number here is a
// by-product of records kept for other reasons — there is no analytics in this
// stack — and a usage figure whose limits are invisible is how somebody ends up
// telling a client something untrue about their own account.

import { getAdminUsage, type AdminUsage, type AdminUsageClient } from "../../data/api.js";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** A date the way the rest of the admin area writes them. */
function shortDay(iso: string | null): string {
  if (iso === null) return "never";
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return "unknown";
  return t.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/** Whole days between then and now, or null when there is no then. */
function daysAgo(iso: string | null): number | null {
  if (iso === null) return null;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return null;
  return Math.floor((Date.now() - t.getTime()) / 86_400_000);
}

/** "3 days ago" / "today" — the reading that answers "is this current?". */
function agoPhrase(iso: string | null): string {
  const d = daysAgo(iso);
  if (d === null) return "never";
  if (d <= 0) return "today";
  if (d === 1) return "yesterday";
  return `${d} days ago`;
}

/** One headline figure. */
function stat(label: string, value: string, note?: string): HTMLElement {
  const box = el("div", "ad-stat");
  box.appendChild(el("div", "ad-stat-v", value));
  box.appendChild(el("div", "ad-stat-l", label));
  if (note !== undefined) box.appendChild(el("div", "ad-stat-n", note));
  return box;
}

/**
 * The sign-in bars.
 *
 * One bar per day across the whole window INCLUDING the empty ones — a series
 * that only plots the days something happened turns a fortnight of silence into
 * a tidy row of bars and hides exactly the thing worth seeing.
 */
function bars(days: AdminUsageClient["days"], window: number): HTMLElement {
  const wrap = el("div", "ad-bars");
  const byDate = new Map(days.map((d) => [d.date, d.count]));
  const peak = days.reduce((m, d) => Math.max(m, d.count), 0);

  const today = new Date();
  for (let i = window - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const n = byDate.get(key) ?? 0;
    const bar = el("div", n > 0 ? "ad-bar" : "ad-bar zero");
    // Floor the drawn height so a day with one sign-in is visibly not zero.
    bar.style.height = peak > 0 && n > 0 ? `${Math.max(8, (n / peak) * 100)}%` : "1px";
    bar.title = `${key} — ${n} sign-in${n === 1 ? "" : "s"}`;
    wrap.appendChild(bar);
  }
  return wrap;
}

/** One client's whole picture. */
function clientCard(c: AdminUsageClient, window: number): HTMLElement {
  const block = el("section", "block");
  const head = el("div", "bh");
  head.appendChild(el("span", "bt", c.name));
  block.appendChild(head);
  const body = el("div", "bb");
  block.appendChild(body);

  // --- the two questions, side by side ---
  const lastSeen = c.accounts.reduce<string | null>(
    (m, a) => (a.last !== null && (m === null || a.last > m) ? a.last : m),
    null,
  );
  const signIns = c.accounts.reduce((n, a) => n + a.sign_ins, 0);

  const row = el("div", "ad-stats");
  row.appendChild(stat("Last sign-in", agoPhrase(lastSeen), shortDay(lastSeen)));
  row.appendChild(stat("Sign-ins on record", String(signIns), "a lower bound — see below"));

  // The headline. A fraction, never a bare count.
  const { tuned, links } = c.adoption;
  const tunedBox = stat(
    "Limits that are theirs",
    links === 0 ? "—" : `${tuned} of ${links}`,
    tuned === 0 && links > 0
      ? "every verdict is graded on our seeded numbers"
      : `last changed ${agoPhrase(c.adoption.last_tuned_at)}`,
  );
  if (tuned === 0 && links > 0) tunedBox.classList.add("ad-stat-warn");
  row.appendChild(tunedBox);
  row.appendChild(
    stat("Reports filed", String(c.adoption.feedback),
      c.adoption.feedback === 0 ? "the path works; nobody has used it" : "in Feedback"),
  );
  body.appendChild(row);

  // --- arrivals over the window ---
  body.appendChild(el("div", "ad-sub", `Sign-ins, last ${window} days`));
  body.appendChild(bars(c.days, window));

  // --- who ---
  if (c.accounts.length === 0) {
    body.appendChild(el("p", "ad-empty", "No accounts under this client."));
  } else {
    const table = el("table", "ad-table");
    const thead = el("thead");
    const hr = el("tr");
    for (const h of ["Account", "Sign-ins", "Active days", "First", "Last"]) {
      hr.appendChild(el("th", undefined, h));
    }
    thead.appendChild(hr);
    table.appendChild(thead);
    const tbody = el("tbody");
    for (const a of c.accounts) {
      const tr = el("tr", a.sign_ins === 0 ? "muted" : undefined);
      const who = el("td");
      who.appendChild(el("span", "mono", a.email));
      if (a.staff) who.appendChild(el("span", "ad-staff", "SeaState"));
      tr.appendChild(who);
      tr.appendChild(el("td", "mono", String(a.sign_ins)));
      tr.appendChild(el("td", "mono", String(a.active_days)));
      tr.appendChild(el("td", "mono", shortDay(a.first)));
      tr.appendChild(el("td", "mono", shortDay(a.last)));
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    body.appendChild(table);
  }

  // --- what exists in the account ---
  body.appendChild(el("div", "ad-sub", "Configured"));
  body.appendChild(
    el(
      "p",
      "note",
      `${c.adoption.vessels} vessels · ${c.adoption.operations} operations · ` +
        `${c.adoption.links} armed pairs · ${c.adoption.prefs_touched} of ` +
        `${c.accounts.length} accounts have changed a setting`,
    ),
  );
  return block;
}

export function mountAdminUsage(host: HTMLElement): void {
  host.replaceChildren();
  const loading = el("p", "ad-empty", "Reading…");
  host.appendChild(loading);

  void getAdminUsage().then((res) => {
    host.replaceChildren();
    if (!res.ok) {
      host.appendChild(
        el("p", "ad-empty", `Could not read usage: ${res.error}`),
      );
      return;
    }
    const data: AdminUsage = res.data;
    if (data.clients.length === 0) {
      host.appendChild(el("p", "ad-empty", "No clients."));
      return;
    }
    for (const c of data.clients) host.appendChild(clientCard(c, data.window));

    // What the numbers are, on the page with them.
    const caveats = el("section", "block");
    const ch = el("div", "bh");
    ch.appendChild(el("span", "bt", "What these numbers are"));
    caveats.appendChild(ch);
    const cb = el("div", "bb");
    const ul = el("ul", "ad-caveats");
    for (const line of [
      "There is no analytics in this stack — no page views, no time on page. " +
        "Everything here is a by-product of records kept for other reasons.",
      "“Sign-ins” counts session rows. A row is written on each successful " +
        "sign-in and deleted on an explicit sign-out, so it is a LOWER BOUND: " +
        "it measures arrivals, never how long anyone stayed.",
      "“Limits that are theirs” is the one that matters. A stored limit IS the " +
        "client's own number and its absence IS our seeded default — there is no " +
        "flag to drift out of step.",
      "“Changed a setting” means somebody opened settings and altered something. " +
        "It says they looked, not that they were satisfied.",
      "Nothing here records IP addresses, user agents or per-request activity. " +
        "This reports on an account, not on a person.",
    ]) {
      ul.appendChild(el("li", undefined, line));
    }
    cb.appendChild(ul);
    caveats.appendChild(cb);
    host.appendChild(caveats);
  });
}
